import * as THREE from 'three'
import Experience from '../experience.js'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js'
import signVertexShader from '../../shaders/yearsignface/vertex.glsl'
import signFragmentShader from '../../shaders/yearsignface/fragment.glsl'
import { PLATFORM_HEIGHT } from './pole-platform.js';
import gsap from 'gsap';

const SignSize = 2.134; // 7 feet tall
const posZ = 6.839; // How far the platform goes out in front of the pole center
const SignDepth = 0.5;

export default class YearSign {

    signSettings = {
        onOff: false,
        lightIntensity: 11,
    }

    constructor(year) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.textureLoader = this.experience.textureLoader;

        // Debug
        this.debugFolder = this.experience.gui.addFolder('Year Sign')
        this.debugFolder.close();

        this.setModel(year)
    }

    setModel(year) {

        const signFaceOffTex = this.textureLoader.load('./textures/sign-face-off-texture.jpg');
        const signFaceOnTex = this.textureLoader.load('./textures/sign-face-on-texture.jpg');
        signFaceOnTex.colorSpace = THREE.SRGBColorSpace;
        signFaceOnTex.generateMipmaps = false; // prevents texture seam
        signFaceOffTex.colorSpace = THREE.SRGBColorSpace;
        signFaceOffTex.generateMipmaps = false; // prevents texture seam

        this.signFaceUniforms = {
            onOff: { type: "f", value: this.signSettings.onOff ? 1.0 : 0.0 },
            brightness: { type: "f", value: 0.6 },
            uTextureOn: { value: signFaceOnTex },
            uTextureOff: { value: signFaceOffTex }
        };

        const signFaceMat = new THREE.ShaderMaterial( {
            uniforms: this.signFaceUniforms,
            vertexShader: signVertexShader,
            fragmentShader: signFragmentShader,
            vertexColors: true
        });


        const startIntensity = this.signSettings.onOff ? this.signSettings.lightIntensity : 0.0;
        this.light = new THREE.PointLight(0xdeb961, startIntensity, 13.5, .7);
        this.light.position.x = 0;
        this.light.position.z = posZ + 1.8; // Place light a little bit in front of sign
        this.light.position.y = SignSize / 2;
        this.scene.add(this.light);

        const fontLoader = new FontLoader(this.experience.loadingManager);
        fontLoader.load('./fonts/New_Years_Party_Regular.json', (font) => {
            const textGeometry = new TextGeometry('' + year, {
                font,
                size: SignSize * 0.9, // The font is a little bigger than it should be, so adjust down 10%
                depth: SignDepth, // Font thickness
                bevelEnabled: false
            })
            textGeometry.center();
            textGeometry.groups = this.calcNewTextMatGroups(textGeometry.groups);

            const signShellMaterial = new THREE.MeshPhongMaterial({ color: '#c39c3e' });
            signShellMaterial.shininess = 50;
            signShellMaterial.specular = new THREE.Color(0x999999);

            this.text = new THREE.Mesh(textGeometry, [ signFaceMat, signShellMaterial ]);
            this.text.position.z = posZ + (SignDepth / 2);
            this.text.position.x = 0.1; // Needs to be offset to be centered
            this.text.position.y = PLATFORM_HEIGHT - SignSize + (SignSize / 2) - 0.2;
            this.scene.add(this.text);
        });

        this.debugFolder.add(this.signSettings, 'onOff').name('On / Off')
            .onChange((value) => {
                if (value) {
                    this.lightOn();
                } else {
                    this.lightOff();
                }
            }).listen();
        this.debugFolder.add(this.signFaceUniforms.brightness, 'value').min(0).max(1).step(0.001).name('Sign Brightness');
        this.debugFolder.add(this.signSettings, 'lightIntensity').min(0).max(40).step(0.001).name('Light Intensity')
        .onChange((value) => {
            if (this.signSettings.onOff) {
                this.light.intensity = value;
            }
        });
        this.debugFolder.add(this.light, 'distance').min(0).max(100).step(0.001).name('Light Distance');
        this.debugFolder.add(this.light, 'decay').min(0).max(4).step(0.001).name('Light Decay');
    }

    lightOn() {

        gsap.to(
            this.signFaceUniforms.onOff,
            { value: 1, duration: 0.08, ease: 'linear' },
        );
        gsap.to(
            this.light,
            { intensity: this.signSettings.lightIntensity,
                duration: 0.08, ease: 'linear' },
        );
        this.signSettings.onOff = true;
    }

    lightOff() {
        gsap.to(
            this.signFaceUniforms.onOff,
            { value: 0, duration: 0.08, ease: 'linear' },
        );
        gsap.to(
            this.light,
            { intensity: 0,
                duration: 0.08, ease: 'linear' },
        );
        this.signSettings.onOff = false;
    }


    update(elapsedTime, timeLeft) {
        
    }

    calcNewTextMatGroups(groups) {
        // Default material groups are 2 for each letter - 1. Front/back  2. The sides
        // We want only the front to be materialIndex 0 (the lit up part)
        const newGroups = [];
        for (let i = 0; i < groups.length; i++) {
            const g = groups[i];
            if (g.materialIndex == 0) {
                // Split in two
                const g1 = { start: g.start, count: g.count / 2, materialIndex: 1 };
                const g2 = { start: g.start + (g.count / 2), count: g.count / 2, materialIndex: 0 };

                if (newGroups.length > 0 && newGroups[newGroups.length-1].materialIndex == g1.materialIndex) {
                    const prevNewGroup = newGroups[newGroups.length-1];
                    // combine with previous instead of adding
                    prevNewGroup.count += g1.count;
                } else {
                    newGroups.push(g1);
                }

                newGroups.push(g2);

            } else {
                newGroups.push(g);
            }
        }
        return newGroups;
    }
}